import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

import { findForm } from '@r-client/shared/util/core';

import { Button, IButtonProps } from '../button';

export interface ISubmitButtonProps
  extends Omit<
    IButtonProps,
    'onError' | 'onSubmit' | 'onSubmitCapture' | 'loading'
  > {
  keepLoadingAfterSubmit?: boolean;
  onSubmit?(e?: Event): Promise<any>;
  onError?(e: unknown): void;
  loading?: boolean;
}

type TState = {
  loading: boolean | undefined;
  disabled: boolean | undefined;
};

export const SubmitButton: FunctionComponent<
  React.PropsWithChildren<ISubmitButtonProps>
> = function SubmitButton({
  children,
  fullWidth,
  type = 'primary',
  onSubmit,
  onError,
  disabled,
  loading,
  keepLoadingAfterSubmit,
  onClick,
  ...props
}) {
  const [state, setState] = useState<TState>({ loading, disabled });

  useEffect(() => {
    setState((prevState) => {
      const newState: Partial<typeof prevState> = {};
      if (prevState.disabled !== disabled) {
        newState.disabled = disabled;
      }
      if (prevState.loading !== loading) {
        newState.loading = loading;
      }
      return Object.keys(newState).length
        ? { ...prevState, ...newState }
        : prevState;
    });
  }, [disabled, loading]);

  const ref = useRef<HTMLButtonElement | null>(null);
  let thisButtonClick = false;
  let linkToForm = false;

  const handleSubmit$ = async (e?: Event) => {
    if (!onSubmit) {
      return;
    }
    const saveState = state;
    setState({ loading: true, disabled: true });
    try {
      await onSubmit(e);
      if (keepLoadingAfterSubmit) {
        setState({ ...saveState, loading: true });
      } else {
        setState(saveState);
      }
    } catch (err) {
      setState(saveState);
      onError?.(err);
    }
  };

  const handleSubmit = (e?: Event) => {
    handleSubmit$(e);
  };

  useEffect(() => {
    if (!onSubmit) {
      return;
    }
    const form = findForm(ref.current);
    if (!form) {
      return;
    }
    linkToForm = true;
    const preventDefault = (e: Event) => {
      if (thisButtonClick) {
        e.preventDefault();
        thisButtonClick = false;
      }
    };

    form.addEventListener('submit', handleSubmit, true);
    form.addEventListener('submit', preventDefault, false);
    return () => {
      linkToForm = false;
      form.removeEventListener('submit', handleSubmit);
      form.removeEventListener('submit', preventDefault);
    };
  });

  return (
    <Button
      ref={ref}
      type={type}
      htmltype="submit"
      fullWidth={fullWidth}
      disabled={state.disabled}
      loading={state.loading}
      onClick={(e) => {
        thisButtonClick = true;
        onClick?.(e);
        if (!linkToForm) {
          handleSubmit(e.nativeEvent);
        }
      }}
      {...props}
    >
      {children}
    </Button>
  );
};
